import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Clock, XCircle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { WithdrawalsAPI } from '../../lib/api';

interface WithdrawalStats {
    pending: { count: number; totalAmount: number };
    approved: { count: number; totalAmount: number };
    processing: { count: number; totalAmount: number };
}

interface WithdrawalAlertsBarProps {
    className?: string;
    onRefresh?: () => void;
}

const formatCurrency = (value: number): string => {
    if (value >= 100000) {
        return `₹${(value / 100000).toFixed(1)}L`;
    } else if (value >= 1000) {
        return `₹${(value / 1000).toFixed(1)}K`;
    }
    return `₹${value.toLocaleString('en-IN')}`;
};

export function WithdrawalAlertsBar({ className = '', onRefresh }: WithdrawalAlertsBarProps) {
    const navigate = useNavigate();
    const [stats, setStats] = useState<WithdrawalStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const fetchStats = async () => {
        try {
            const response = await WithdrawalsAPI.getStats();
            if (response.success && response.data?.byStatus) {
                setStats({
                    pending: response.data.byStatus.pending || { count: 0, totalAmount: 0 },
                    approved: response.data.byStatus.approved || { count: 0, totalAmount: 0 },
                    processing: response.data.byStatus.processing || { count: 0, totalAmount: 0 },
                });
            }
        } catch (err) {
            console.error('Error fetching withdrawal stats:', err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchStats();
        // Refresh every 60 seconds
        const interval = setInterval(fetchStats, 60000);
        return () => clearInterval(interval);
    }, []);

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchStats();
        onRefresh?.();
    };

    // Don't render if dismissed, loading, or no pending items
    if (dismissed || loading || !stats) return null;

    const totalPending = stats.pending.count + stats.approved.count + stats.processing.count;
    if (totalPending === 0) return null;

    // Determine urgency level
    const urgencyLevel = stats.pending.count > 10 ? 'high' : stats.pending.count > 5 ? 'medium' : 'low';

    const urgencyConfig = {
        high: {
            gradient: 'from-red-500 via-red-600 to-orange-500',
            textColor: 'text-white',
            icon: AlertTriangle,
            pulse: true,
        },
        medium: {
            gradient: 'from-amber-500 via-orange-500 to-yellow-500',
            textColor: 'text-white',
            icon: Clock,
            pulse: true,
        },
        low: {
            gradient: 'from-blue-500 via-blue-600 to-indigo-500',
            textColor: 'text-white',
            icon: Clock,
            pulse: false,
        },
    };

    const config = urgencyConfig[urgencyLevel];
    const Icon = config.icon;

    return (
        <div
            className={`
                relative overflow-hidden rounded-xl
                bg-gradient-to-r ${config.gradient}
                shadow-lg ${config.pulse ? 'animate-pulse-slow' : ''}
                ${className}
            `}
        >
            {/* Animated background pattern */}
            <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]" />
            </div>

            {/* Shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent shimmer-animation" />

            <div className="relative px-4 py-3 sm:px-6 sm:py-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    {/* Left side - Alert info */}
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg bg-white/20 backdrop-blur-sm ${config.pulse ? 'animate-bounce-slow' : ''}`}>
                            <Icon className={`h-5 w-5 ${config.textColor}`} />
                        </div>
                        <div className={config.textColor}>
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-lg">
                                    {stats.pending.count} Pending Withdrawal{stats.pending.count !== 1 ? 's' : ''}
                                </span>
                                {stats.approved.count > 0 && (
                                    <span className="px-2 py-0.5 text-xs rounded-full bg-white/20">
                                        +{stats.approved.count} approved
                                    </span>
                                )}
                                {stats.processing.count > 0 && (
                                    <span className="px-2 py-0.5 text-xs rounded-full bg-white/20">
                                        +{stats.processing.count} processing
                                    </span>
                                )}
                            </div>
                            <p className="text-sm opacity-90">
                                Total: {formatCurrency(stats.pending.totalAmount + stats.approved.totalAmount + stats.processing.totalAmount)} awaiting action
                            </p>
                        </div>
                    </div>

                    {/* Right side - Actions */}
                    <div className="flex items-center gap-2 self-end sm:self-auto">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className={`${config.textColor} hover:bg-white/20 border-0`}
                        >
                            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate('/earnings/withdrawals?status=pending')}
                            className="bg-white/90 hover:bg-white text-gray-900 font-medium shadow-sm"
                        >
                            Review Now
                            <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDismissed(true)}
                            className={`${config.textColor} hover:bg-white/20 border-0 px-2`}
                        >
                            <XCircle className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Progress bar showing pending vs total */}
            <div className="h-1 bg-white/20">
                <div
                    className="h-full bg-white/60 transition-all duration-500"
                    style={{ width: `${(stats.pending.count / Math.max(totalPending, 1)) * 100}%` }}
                />
            </div>

            {/* Custom animations */}
            <style>{`
                @keyframes pulse-slow {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.95; }
                }
                .animate-pulse-slow {
                    animation: pulse-slow 3s ease-in-out infinite;
                }
                @keyframes bounce-slow {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-2px); }
                }
                .animate-bounce-slow {
                    animation: bounce-slow 2s ease-in-out infinite;
                }
                @keyframes shimmer {
                    0% { transform: translateX(-100%); }
                    100% { transform: translateX(100%); }
                }
                .shimmer-animation {
                    animation: shimmer 3s ease-in-out infinite;
                }
            `}</style>
        </div>
    );
}

export default WithdrawalAlertsBar;
